import { use, useEffect, useState } from "react";
import BottomSheetContainer from "../BottomSheet/BottomSheetContainer";
import SubOption from "../SubOption/SubOption";
import Button from "../Button/Button";
import CheckBox from "../Checkbox/CheckBox";
import RadioButton from "../RadioButton/RadioButton";

interface Props {
  questionType: string;
  value: string;
  setSelectedOptions: (value: string[]) => void;
  selectedOptions: string[];
  subOptions?: any[];
  setIsEdited: React.Dispatch<React.SetStateAction<boolean>>;
  othersValue: string;
  setOthersValue: (value: string) => void;
  subOptionOthersValue: string;
  setSubOptionOthersValue: (value: string) => void;
  selectedSubOptions: string[];
  setSelectedSubOptions: (value: string[]) => void;
  tag: string;
  setEnabledSectionsCount: React.Dispatch<React.SetStateAction<number>>;
  accordionId: number;
  setIsSubOptionNotSelected: React.Dispatch<React.SetStateAction<boolean>>;
}

const Option: React.FC<Props> = ({
  questionType,
  value,
  setSelectedOptions,
  selectedOptions,
  subOptions,
  setIsEdited,
  othersValue,
  setOthersValue,
  subOptionOthersValue,
  setSubOptionOthersValue,
  selectedSubOptions,
  setSelectedSubOptions,
  tag,
  setEnabledSectionsCount,
  accordionId,
  setIsSubOptionNotSelected,
}) => {
  const [showSubOptionsBS, setShowSubOptionsBS] = useState<boolean>(false);
  const [optionOthersInputError, setOptionOthersInputError] =
    useState<string>("");

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (selectedOptions?.[0] === value && subOptions?.length) {
      subOptions?.forEach(
        ({ value, is_selected: isSelected, others_answer: othersAnswer }) => {
          if (isSelected) {
            setSelectedSubOptions([value]);
            if (value === "Others") {
              setSubOptionOthersValue(othersAnswer);
            }
          }
        }
      );
    }
  }, [JSON.stringify(subOptions), JSON.stringify(selectedOptions)]);

  useEffect(() => {
    if (selectedOptions?.includes("Others") && value === "Others") {
      if (othersValue) {
        setOptionOthersInputError("");
      } else {
        setOptionOthersInputError("Please enter at least 1 character");
        setEnabledSectionsCount(accordionId + 1);
      }
    }
  }, [JSON.stringify(selectedOptions), value, othersValue]);

  useEffect(() => {
    if (selectedOptions.includes(value)) {
      if (subOptions?.length && !selectedSubOptions.length) {
        setIsSubOptionNotSelected(true);
      } else {
        setIsSubOptionNotSelected(false);
      }
    }
  }, [
    JSON.stringify(selectedSubOptions),
    JSON.stringify(subOptions),
    JSON.stringify(selectedOptions),
    value,
  ]);

  return (
    <div className="mb-[14px]">
      {showSubOptionsBS && (
        <BottomSheetContainer
          open={showSubOptionsBS}
          bottomSheetStyle={{ padding: "20px" }}
          close={() => {
            if (
              selectedSubOptions?.[0] === "Others"
                ? !!subOptionOthersValue
                : true
            ) {
              setShowSubOptionsBS(false);
            }
          }}
        >
          <p className="h5-20-b">Choose type</p>
          <div className="mt-10">
            {subOptions?.map(
              (
                {
                  value,
                  is_checked: isChecked,
                }: {
                  value: string;
                  is_checked: boolean;
                },
                index
              ) => (
                <SubOption
                  questionType={questionType}
                  isCheckedInitialValue={isChecked}
                  value={value}
                  setSelectedSubOptions={setSelectedSubOptions}
                  selectedSubOptions={selectedSubOptions}
                  isLast={index === subOptions?.length - 1}
                  setSubOptionOthersValue={setSubOptionOthersValue}
                  subOptionOthersValue={subOptionOthersValue}
                  setIsEdited={setIsEdited}
                  setEnabledSectionsCount={setEnabledSectionsCount}
                  accordionId={accordionId}
                />
              )
            )}
            <Button
              className="w-full mt-6"
              disabled={
                !selectedSubOptions?.length ||
                (selectedSubOptions?.[0] === "Others" && !subOptionOthersValue)
              }
              onClick={() => {
                setShowSubOptionsBS(false);
              }}
            >
              Confirm
            </Button>
          </div>
        </BottomSheetContainer>
      )}
      <button
        className="flex mb-[14px]"
        onClick={(e) => {
          e.preventDefault();
          setIsEdited(true);
          if (questionType === "single-option") {
            setSelectedOptions([value]);
            setSelectedSubOptions([]);
          } else if (questionType === "multiple-option") {
            if (selectedOptions?.includes(value)) {
              setSelectedOptions(
                selectedOptions?.filter((option) => option !== value)
              );
            } else {
              setSelectedOptions([...(selectedOptions || []), value]);
            }
          } else if (questionType === "declaration") {
            if (selectedOptions?.includes(tag)) {
              setSelectedOptions(
                selectedOptions?.filter((option) => option !== tag)
              );
            } else {
              setSelectedOptions([...(selectedOptions || []), tag]);
            }
          }
        }}
      >
        {questionType === "single-option" ? (
          <RadioButton isActive={value === selectedOptions?.[0]} />
        ) : (
          <CheckBox
            isActive={
              questionType === "multiple-option"
                ? selectedOptions?.includes(value)
                : questionType === "declaration"
                ? selectedOptions?.includes(tag)
                : false
            }
          />
        )}
        <label className="ml-3 text-gray-600 pointer-events-none text-left">
          {value}
        </label>
      </button>
      {selectedOptions?.includes("Others") && value === "Others" ? (
        <div>
          <input
            className={`w-full py-3 px-[10px] h-12 rounded-xl border ${
              optionOthersInputError
                ? "border-status-negative"
                : "border-grey-light"
            } focus:outline-none`}
            value={othersValue}
            onChange={(e) => {
              setIsEdited(true);
              setOthersValue(e.target.value?.trimStart());
            }}
            onKeyDown={handleKeyDown}
          />
          {optionOthersInputError ? (
            <p className="text-12-r mt-1 text-status-negative">
              {optionOthersInputError}
            </p>
          ) : null}
        </div>
      ) : null}
      {selectedOptions?.[0] === value && subOptions?.length ? (
        <div>
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowSubOptionsBS(true);
            }}
            className="w-full flex items-center justify-between px-[10px] py-3 text-14-sb text-left border border-grey-light rounded-xl"
          >
            <span>
              {selectedSubOptions?.length
                ? `${
                    selectedSubOptions?.[0] === "Others"
                      ? `${selectedSubOptions?.[0]} - ${subOptionOthersValue}`
                      : selectedSubOptions?.[0]
                  }`
                : "Choose Type"}
            </span>
            <svg
              className={`transform transition-transform bg-white w-5 h-5 rounded-2xl`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 9l6 6 6-6"
              />
            </svg>
          </button>
          {!selectedSubOptions.length && (
            <p className="text-12-r text-status-negative mt-[10px]">
              Type selection is required
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Option;
